import { Typography } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useContext, useMemo } from 'react';
import { Sheet } from 'react-modal-sheet';
import { AppContext } from '../../../../App';
import { formatToINR } from '../../../../Utils/utils';
import sibLogo from '../../../../Assets/sib/sib_logo.webp';
import styles from './index.module.css';

// The API sends dateOfTransaction with a +00:00 offset; render it in UTC so the
// transaction's own date is shown as-is, not shifted to the device timezone.
dayjs.extend(utc);

const SIBTransactionDetails: React.FC<{
  closeSheet: () => void;
  transaction: any;
}> = ({ closeSheet, transaction }) => {
  const { data }: any = useContext(AppContext);

  const account = useMemo(() => {
    if (data?.current?.ACCOUNT_DETAILS?.accountProfileList) {
      const match = data.current.ACCOUNT_DETAILS.accountProfileList.filter(
        (p: any) => p.account.id === transaction.accountId
      );
      return match.length > 0 ? match[0].account : null;
    }
    return null;
  }, [transaction]);

  return (
    <Sheet
      className='accountDetailsSheet'
      isOpen={true}
      detent='content-height'
      onClose={closeSheet}
    >
      <Sheet.Backdrop onTap={closeSheet} />
      <Sheet.Container>
        <div className={styles.container}>
          <div className={styles.closeIconContainer}>
            <div className={styles.closeIcon} onClick={closeSheet}>
              <Typography.Text className={styles.closeText}>X</Typography.Text>
            </div>
          </div>

          <Typography.Text className={styles.title}>
            Transaction Details
          </Typography.Text>

          {transaction?.narration && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Narration</Typography.Text>
              <Typography.Text className={styles.value}>
                {transaction.narration}
              </Typography.Text>
            </div>
          )}

          {transaction?.amount && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Amount</Typography.Text>
              <Typography.Text className={styles.value}>
                {formatToINR(transaction.amount)}
              </Typography.Text>
            </div>
          )}

          {transaction?.type && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Type</Typography.Text>
              <Typography.Text className={styles.value}>
                {transaction.type}
              </Typography.Text>
            </div>
          )}

          {transaction?.dateOfTransaction && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Date</Typography.Text>
              <Typography.Text className={styles.value}>
                {/* Show the date exactly as it comes in the API timestamp - no
                    timezone conversion. .utc() keeps the raw date/time of the
                    +00:00 value instead of shifting it to the device timezone. */}
                {dayjs.utc(transaction.dateOfTransaction).format("D MMM'YY")}
              </Typography.Text>
            </div>
          )}

          {transaction?.counterParty && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Counterparty</Typography.Text>
              <Typography.Text className={styles.value}>
                {transaction.counterParty}
              </Typography.Text>
            </div>
          )}

          {transaction?.paymentMode && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Payment Mode</Typography.Text>
              <Typography.Text className={styles.value}>
                {transaction.paymentMode.replace(/_/g, ' ')}
              </Typography.Text>
            </div>
          )}

          {account?.fipId && account?.bank && (
            <div className={styles.row}>
              <Typography.Text className={styles.label}>Bank</Typography.Text>
              <div className={styles.bankRow}>
                <div className={styles.bankLogo}>
                  <img
                    style={{ height: 16, width: 'auto', objectFit: 'contain' }}
                    src={account.fipId === 'SIB' ? sibLogo : `https://logo.ignosis.ai/${account.fipId}.jpeg`}
                    alt={account.bank}
                  />
                </div>
                <Typography.Text className={styles.value}>
                  {account.bank}
                </Typography.Text>
              </div>
            </div>
          )}
        </div>
      </Sheet.Container>
    </Sheet>
  );
};

export default SIBTransactionDetails;
